import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Navbar, Footer } from '../components/Layout';
import EmailSignupForm from '../components/growth/EmailSignupForm';
import { PostCard } from './InsightsPage';
import { Clock, ArrowRight, Linkedin, Twitter, Facebook, Link2, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '');

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const injectJsonLd = (id, data) => {
  document.getElementById(id)?.remove();
  const s = document.createElement('script');
  s.id = id;
  s.type = 'application/ld+json';
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/blog/posts/${slug}`);
      setData(r.data);
    } catch {
      setNotFound(true);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data?.post) return;
    const p = data.post;
    const seo = p.seo || {};
    const url = `${window.location.origin}/insights/${p.slug}`;
    document.title = seo.seo_title || `${p.title} | FuturePath Insights`;
    setMeta('description', seo.meta_description || p.summary);
    if (seo.no_index) setMeta('robots', 'noindex,nofollow');
    setMeta('og:title', seo.og_title || p.title, 'property');
    setMeta('og:description', seo.og_description || p.summary, 'property');
    setMeta('og:image', seo.og_image || p.featured_image, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:url', seo.canonical_url || url, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seo.og_title || p.title);
    setMeta('twitter:description', seo.og_description || p.summary);
    setMeta('twitter:image', seo.og_image || p.featured_image);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = seo.canonical_url || url;

    injectJsonLd('ld-article', {
      '@context': 'https://schema.org', '@type': 'Article', headline: p.title,
      description: p.summary, image: p.featured_image ? [p.featured_image] : undefined,
      datePublished: p.published_at, dateModified: p.updated_at,
      author: { '@type': 'Person', name: p.author_name },
      publisher: { '@type': 'Organization', name: 'Agentic FuturePath AI',
                   logo: { '@type': 'ImageObject', url: `${window.location.origin}/fp-logo.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      articleSection: p.category, keywords: (p.tags || []).join(', '),
    });
    injectJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Insights', item: `${window.location.origin}/insights` },
        { '@type': 'ListItem', position: 2, name: p.category,
          item: `${window.location.origin}/insights/category/${(p.category || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` },
        { '@type': 'ListItem', position: 3, name: p.title, item: url },
      ],
    });
  }, [data]);

  const share = (network) => {
    const url = `${window.location.origin}/insights/${slug}?utm_source=${network}&utm_medium=social`;
    const text = encodeURIComponent(data?.post?.title || '');
    const targets = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      x: `https://x.com/intent/post?text=${text}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    axios.post(`${API}/analytics/event`, { event: 'social_share_click', path: `/insights/${slug}`, label: network }).catch(() => {});
    if (network === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Link copied');
      return;
    }
    window.open(targets[network], '_blank', 'noopener');
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-navy-gradient">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-32 pb-20 text-center" data-testid="article-not-found">
          <h1 className="font-outfit text-3xl font-bold text-white">Article not found</h1>
          <p className="text-slate-300 mt-2">It may have moved or been unpublished.</p>
          <Link to="/insights" className="btn-primary inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full text-sm font-semibold">
            Back to Insights <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-navy-gradient">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-32 pb-20 text-slate-300">Loading…</div>
      </div>
    );
  }

  const p = data.post;
  const link = data.product_link;

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20" data-testid="article-page">
        <nav className="flex items-center gap-1.5 text-[11px] text-slate-300" aria-label="Breadcrumb">
          <Link to="/insights" className="hover:text-cyan-300">Insights</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/insights/category/${(p.category || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="hover:text-cyan-300">{p.category}</Link>
        </nav>

        <h1 className="font-outfit text-4xl sm:text-5xl font-bold tracking-tight text-white mt-4">{p.title}</h1>
        {p.subtitle && <p className="text-base md:text-lg text-slate-300 mt-3">{p.subtitle}</p>}

        <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-slate-300">
          <Link to={`/insights/author/${p.author_slug}`} className="text-slate-300 hover:text-cyan-300">
            {p.author_name}
          </Link>
          <span>{fmt(p.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time} min read</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => share('linkedin')} data-testid="share-linkedin" className="text-slate-300 hover:text-cyan-300"><Linkedin className="w-4 h-4" /></button>
            <button onClick={() => share('x')} data-testid="share-x" className="text-slate-300 hover:text-cyan-300"><Twitter className="w-4 h-4" /></button>
            <button onClick={() => share('facebook')} data-testid="share-facebook" className="text-slate-300 hover:text-cyan-300"><Facebook className="w-4 h-4" /></button>
            <button onClick={() => share('copy')} data-testid="share-copy" className="text-slate-300 hover:text-cyan-300"><Link2 className="w-4 h-4" /></button>
          </div>
        </div>

        {p.featured_image && (
          <img src={p.featured_image} alt={p.featured_image_alt || p.title}
            className="w-full rounded-2xl mt-7 border border-slate-700/60" />
        )}

        <div className="blog-content mt-8" data-testid="article-content"
          dangerouslySetInnerHTML={{ __html: p.content }} />

        {link && (
          <Link to={link.href} data-testid="article-product-link"
            onClick={() => axios.post(`${API}/analytics/event`, { event: 'platform_cta_click', path: `/insights/${slug}`, label: link.href }).catch(() => {})}
            className="flex items-center justify-between gap-4 mt-10 rounded-2xl border border-slate-700/70 bg-slate-900/40 p-5 hover:border-cyan-500/40 transition-colors duration-200">
            <div>
              <p className="text-sm font-semibold text-white">See this inside the platform</p>
              <p className="text-xs text-slate-300 mt-0.5">{link.label}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-cyan-300" />
          </Link>
        )}

        <div className="mt-8">
          <EmailSignupForm variant="full" source="article"
            heading={p.cta_label || 'Join the waitlist'}
            sub="Get new guides and early access news from Agentic FuturePath AI." />
        </div>

        {p.author_bio && (
          <div className="mt-10 rounded-2xl border border-slate-700/70 bg-slate-900/40 p-5" data-testid="article-author">
            <p className="text-sm font-semibold text-white">{p.author_name}</p>
            <p className="text-sm text-slate-300 mt-1">{p.author_bio}</p>
          </div>
        )}

        {(data.related || []).length > 0 && (
          <div className="mt-12" data-testid="article-related">
            <h2 className="text-base md:text-lg font-semibold text-white">Keep reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
              {data.related.map((r) => <PostCard key={r.slug} post={r} />)}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
